import { ActorPipe } from './shared/actor-display';
import { LinkLandingComponent } from './link-landing/link-landing.component';
import { CompanyRefreshInterceptor } from './auth/company-refresh.interceptor';
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { I18nModule } from './i18n/i18n.module';
import { AppComponent } from './app.component';
import { ProjectGridModule } from './project-grid/project-grid.module';
import { ProjectSelectionComponent } from './project-selection/project-selection.component';
import { HeatmapComponent } from './heatmap/heatmap.component';
import { FormsModule } from '@angular/forms';
import { TemperatureComponent } from './temperature/temperature.component';
import { ChartScreenshotDirective } from './charts/chart-screenshot.directive';

import { Screen3Component } from './screen3/screen3.component';
import { GraphComponent } from './graph/graph.component';
import { CuringComponent } from './curing/curing.component';
import { CuringBandHatchComponent } from './curing/curing-band-hatch.component';
import { SubviewsComponent } from './subviews/subviews.component';
import { HealthComponent } from './health/health.component';
import { ProjectComponent } from './project/project.component';
import { ProjectInformationComponent } from './project-information/project-information.component';
import { LogoutComponent } from './logout/logout.component';

// HTTP modules required by MSAL
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';

// Required for MSAL
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
} from '@azure/msal-browser';
import {
  MsalGuard,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_BROADCAST_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
  MsalRedirectComponent,
} from '@azure/msal-angular';
import { LoginComponent } from './login/login.component';
import { UnauthorizedComponent } from './unauthorized/unauthorized.component';
import { MainLayoutComponent } from './main-layout/main-layout.component';
import { ExportProjectComponent } from './export-project/export-project.component';
import { CustomerSelectorComponent } from './customer-selector/customer-selector.component';
import { NotificationBellComponent } from './notifications/notification-bell.component';
import { ToastHostComponent } from './notifications/toast-host.component';
import { OfflineHatchComponent } from './health/offline-hatch.component';
import { AlertsComponent } from './alerts/alerts.component';
import { CureSheetsComponent } from './cure-sheets/cure-sheets.component';
import { CureSheetListComponent } from './cure-sheets/cure-sheet-list.component';
import { environment } from '../environments/environment';
import {
  VERICURE_API_SCOPE,
  GRAPH_SCOPES,
  requireEntraAuth,
  apiResourceUrl,
} from './auth/api-scope';
import { VericureAuthInterceptor } from './auth/vericure-auth.interceptor';

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      // 'Application (client) ID' of app registration in the Microsoft Entra admin center - this value is a GUID
      //clientId: '621f284b-5a6f-4091-846e-d849199ef97b',
      // My Tenant - Multi Personal
      //clientId: 'daad6de0-17d9-4925-bfaa-9f845007f085',
      // Per-environment override via environment.entraClientId: prod2 declares
      // 'VeriCure', beta-dev declares 'VeriCure-test', both in RedHaw's tenant
      // (d900d985).
      //
      // THIS FALLBACK MUST EQUAL THE ONE IN auth/api-scope.ts. That file derives
      // the requested scope from the same field, and a token whose audience is
      // not the app the user actually signed into is refused. Two literals that
      // have to agree is a latent bug; they are kept together only by this note
      // and the matching one there.
      clientId:
        (environment as { entraClientId?: string }).entraClientId ??
        '2e945b52-de34-4243-a63c-b68cb91c662b',
      // Full directory URL, in the form of https://login.microsoftonline.com/<tenant>
      //authority: 'https://login.microsoftonline.com/83310ec5-e3da-4db5-a5f8-71d571cc6d02/',
      authority: 'https://login.microsoftonline.com/common/',
      // Must be the same redirectUri as what was provided in your app registration.
      redirectUri: environment.redirectUri,
      // Optional per environment; MSAL falls back to redirectUri when absent, so
      // omitting it in the shipped configs keeps their behaviour unchanged.
      ...((environment as { postLogoutRedirectUri?: string }).postLogoutRedirectUri
        ? {
            postLogoutRedirectUri: (environment as { postLogoutRedirectUri?: string })
              .postLogoutRedirectUri,
          }
        : {}),
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE,
    },
  });
}

/**
 * Resources MsalInterceptor attaches a bearer token to.
 *
 * Graph is always mapped. The Vericure API is mapped only while
 * requireEntraAuth() is true — see the flag's doc for the cutover story. Note MSAL
 * issues ONE token per resource, so these are separate entries with separate
 * scopes; a single token cannot span Graph and our API.
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', GRAPH_SCOPES);

  if (requireEntraAuth()) {
    // Matched by prefix, so this covers every VericureDataServices endpoint under
    // the configured API base (beta-dev in the beta-dev build, prod in prod).
    protectedResourceMap.set(apiResourceUrl(), [VERICURE_API_SCOPE]);
  }

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

/**
 * Scopes requested at interactive sign-in.
 *
 * Including the API scope here means the API token is already in the MSAL cache
 * after login, so the first VericureDataServices call resolves silently instead of
 * paying a round trip. The SPA is pre-authorized on the API app registration, so
 * this adds no consent prompt.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: requireEntraAuth()
        ? [...GRAPH_SCOPES, VERICURE_API_SCOPE]
        : [...GRAPH_SCOPES],
    },
  };
}

@NgModule({
  declarations: [
    // The camera button on all four chart screens. A DIRECTIVE, not a component:
    // `a.link7` is styled per-screen under emulated encapsulation, so moving the
    // anchor into a child component would lose the icon on every one of them.
    ChartScreenshotDirective,
    ProjectSelectionComponent,
    AppComponent,
    HeatmapComponent,
    TemperatureComponent,
    Screen3Component,
    GraphComponent,
    CuringComponent,
    CuringBandHatchComponent,
    SubviewsComponent,
    HealthComponent,
    ProjectComponent,
    ProjectInformationComponent,
    LinkLandingComponent,
    LogoutComponent,
    LoginComponent,
    UnauthorizedComponent,
    MainLayoutComponent,
    ExportProjectComponent,
    CustomerSelectorComponent,
    NotificationBellComponent,
    ToastHostComponent,
    OfflineHatchComponent,
    AlertsComponent,
    CureSheetsComponent,
    CureSheetListComponent,
  ],
  imports: [
    ActorPipe,
    I18nModule,
    ProjectGridModule,
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    FormsModule,
    MsalModule,
  ],
  providers: [
    {
      // Replaces the stock MsalInterceptor, which escalates to an INTERACTIVE
      // Microsoft redirect whenever it cannot get a token silently. Vericure
      // Hybrid also signs users in with Google and email+password; those sessions
      // have no MSAL account, so the stock interceptor would bounce them to a
      // Microsoft login on their first API call once VericureDataServices URLs are
      // in protectedResourceMap. VericureAuthInterceptor attaches the bearer only
      // when an account exists and otherwise falls through to the function key.
      provide: HTTP_INTERCEPTORS,
      useClass: VericureAuthInterceptor,
      multi: true,
    },
    // Refresh the switchable companies when the server says the active one is
    // not the caller's. Never retries or hides the error.
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CompanyRefreshInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      // REPLAY THE LOGIN EVENT.
      //
      // msalSubject$ defaults to a plain Subject, so only whoever is already
      // subscribed hears LOGIN_SUCCESS. UserAuthService is providedIn:'root' and
      // constructed lazily — by the first component that injects it — which can
      // easily be AFTER handleRedirectPromise has resolved on the return leg of a
      // sign-in. It would then miss the one event that says which account the user
      // picked, and fall back to reconciling from storage.
      //
      // A short replay buffer makes the anchoring deterministic instead of
      // dependent on component construction order.
      provide: MSAL_BROADCAST_CONFIG,
      useValue: { eventsToReplay: 4 },
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}
